import { Typography } from "@material-ui/core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { totalWidgetStyles } from "./TotalWidgetStyles";
import { TotalWidgetProps } from "./TotalWidgetProps";
import {
    ActiveElement,
    Chart,
    ChartEvent,
    Plugin,
    ChartOptions,
    ChartData,
} from "chart.js";
import { handleArcFading } from "helpers/handleArcFading";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";

// useMemo is here because of chart.js bug that triggers chart rerender every time the state/props of component change
// even if the change doesn't affect the chart
export default function TotalWidget({
    title,
    centerTitle,
    centerSubtitle,
    showTooltips,
    segments,
    isLoading = false,
}: TotalWidgetProps) {
    const classes = totalWidgetStyles();

    const totalCount = segments
        .map((s) => s.count)
        .reduce((acc, curr) => acc + curr, 0);

    const [segmentIndex, setSegmentIndex] = useState<number | null>(null);

    const dataset = useMemo(
        () => ({
            data: segments.map((s) => s.count / totalCount),
            backgroundColor: segments.map((s) => s.color),
            borderWidth: 0,
            cutout: "0%",
            hoverBackgroundColor: segments.map((s) => s.color),
        }),
        [segments]
    );

    const data: ChartData<"doughnut"> = {
        datasets: [
            { ...dataset, weight: 15 },
            // to trigger hover effect inside the doughnut too
            {
                ...dataset,
                weight: 85,
                backgroundColor: "transparent",
                hoverBackgroundColor: "transparent",
            },
        ],
    };

    const options: ChartOptions = {
        plugins: {
            tooltip: {
                enabled: false,

                external({ chart, tooltip }) {
                    if (!showTooltips) {
                        return;
                    }

                    let tooltipEl = document.getElementById("chartjs-tooltip");

                    if (!tooltipEl) {
                        tooltipEl = document.createElement("div");
                        tooltipEl.id = "chartjs-tooltip";
                        document.body.appendChild(tooltipEl);
                    }

                    if (
                        tooltip.opacity === 0 ||
                        chart.getActiveElements().length === 0
                    ) {
                        tooltipEl.style.opacity = "0";
                        return;
                    }

                    const segment =
                        segments[chart.getActiveElements()[0].index];

                    const canvasPosition = chart.canvas.getBoundingClientRect();

                    const left =
                        canvasPosition.left +
                        window.pageXOffset -
                        tooltipEl.offsetWidth;

                    const top = canvasPosition.top + window.pageYOffset;

                    tooltipEl.innerHTML = `
                            <span class="tooltip-title">${segment.tooltipTitle}</span>
                            <span class="tooltip-subtext">${segment.tooltipSubtitle}</span>
                            `;
                    tooltipEl.style.opacity = "1";
                    tooltipEl.style.left = `${left}px`;
                    tooltipEl.style.top = `${top}px`;
                },
            },
        },

        onHover: (
            event: ChartEvent,
            elements: ActiveElement[],
            chart: Chart
        ) => {
            const activeSegmentIndex = elements[0] ? elements[0].index : null;

            handleArcFading(chart, activeSegmentIndex);
            setSegmentIndex(activeSegmentIndex);
        },
    };

    const plugins: Plugin[] = [
        {
            id: "catchMouseOut",
            // This is needed to correctly handle fading logic if the user moves the mouse too fast
            afterEvent(chart, { event }) {
                // timeout is needed otherwise the event is often not fired
                setTimeout(() => {
                    if (event.type === "mouseout") {
                        handleArcFading(chart, null);
                        setSegmentIndex(null);
                    }
                }, 100);
            },
        },
    ];

    const containerRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        if (containerRef.current === null) {
            return;
        }

        const chartInstance = new Chart(containerRef.current, {
            type: "doughnut",
            data,
            plugins,
            options,
        });

        chartInstance.update();
        return () => chartInstance.destroy();
        // TODO: update chart on segments update without creating new instance
    }, [segments]);

    return (
        <div className={classes.widgetContainer}>
            <Typography className={classes.title}>{title}</Typography>
            <div className={classes.widget}>
                {isLoading ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        <canvas ref={containerRef} />
                        <div className={classes.textContainer}>
                            <Typography
                                className={classes.mainText}
                                component="span"
                            >
                                {segmentIndex === null
                                    ? centerTitle
                                    : segments[segmentIndex].centerTitle}
                            </Typography>
                            <Typography
                                variant="subtitle2"
                                component="span"
                                className={classes.subText}
                            >
                                {segmentIndex === null
                                    ? centerSubtitle
                                    : segments[segmentIndex].centerSubtitle}
                            </Typography>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
